/**
 * Right-side Label Properties panel.
 *
 * Mirrors the Symbology panel's editing model exactly — draft / session /
 * committed — so the two behave the same way, and deliberately shares its
 * field controls rather than growing a parallel set. The two configurations
 * stay independent: nothing here reads or writes `layer.renderer`.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import type { AppModule, Layer, LayerGeometryType } from '../types';
import { getLayerCapabilities, getLayerDisplayName } from '../layers/capabilities';
import Tooltip, { InfoHint } from '../components/glass/Tooltip';
import { resolveGeometryType } from '../layers/geometry';
import { buildLabelSld, isSldTooLarge } from '../layers/sld';
import {
  ColorField,
  PropertySection,
  SliderField,
  ToggleField,
} from '../components/layers/symbology/SymbolProperties';
import {
  LABEL_FONTS,
  PLACEMENTS_BY_GEOMETRY,
  createLabeling,
  defaultPlacementFor,
  normaliseLabeling,
  type LabelClass,
  type LabelFilterOperator,
  type LayerLabeling,
} from './LabelTypes';
import { extractExpressionFields } from './LabelExpression';
import { describeLabelingMode, fetchLabelFields, type LabelField } from './LabelService';
import {
  SCALE_PRESETS,
  describeScaleProblem,
  formatScale,
  parseScale,
} from './ScaleVisibility';

/** Preview debounce. A WMS label change costs a tile round-trip, so edits are
 *  batched; the canvas path is cheap enough that the same delay is invisible. */
const PREVIEW_DEBOUNCE_MS = 300;

const FONT_WEIGHTS: { id: LabelClass['text']['fontWeight']; label: string }[] = [
  { id: 'normal', label: 'Normal' },
  { id: '500', label: 'Medium' },
  { id: '600', label: 'Semi Bold' },
  { id: 'bold', label: 'Bold' },
];

const FILTER_OPERATORS: { id: LabelFilterOperator; label: string }[] = [
  { id: '=', label: 'is' },
  { id: '!=', label: 'is not' },
  { id: '>', label: 'greater than' },
  { id: '<', label: 'less than' },
  { id: 'contains', label: 'contains' },
];

export default function LabelProperties({
  layer,
  module,
  open,
  currentScale,
  onPreview,
  onApply,
  onGeometryResolved,
  onClose,
}: {
  layer: Layer | null;
  module: AppModule;
  open: boolean;
  /** Live map scale denominator, so "use current scale" means what it says. */
  currentScale: number;
  /** Live preview — not persisted. */
  onPreview: (labeling: LayerLabeling | null) => void;
  /** Commit. */
  onApply: (labeling: LayerLabeling) => void;
  onGeometryResolved?: (layerId: string, geometryType: LayerGeometryType) => void;
  onClose: () => void;
}) {
  const [resolvedGeometry, setResolvedGeometry] = useState<LayerGeometryType>(
    layer?.geometryType ?? 'unknown',
  );
  const [resolving, setResolving] = useState(false);
  const geometry = resolvedGeometry;

  const labelingMode = useMemo(
    () => (layer ? getLayerCapabilities(layer, module).labelingMode : 'none'),
    [layer, module],
  );

  const committed = useMemo<LayerLabeling>(
    () => normaliseLabeling(layer?.labeling, geometry),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layer?.id, layer?.labeling, geometry],
  );

  const [sessionLabeling, setSessionLabeling] = useState<LayerLabeling>(committed);
  const [draft, setDraft] = useState<LayerLabeling>(committed);
  const [fields, setFields] = useState<LabelField[]>([]);
  const [dirty, setDirty] = useState(false);

  /** The single class the current UI edits. The model holds a list so more can
   *  be added later without a migration; the panel simply edits the first. */
  const labelClass = draft.classes[0];

  const previewTimer = useRef<number | null>(null);
  const clearPreviewTimer = () => {
    if (previewTimer.current) {
      window.clearTimeout(previewTimer.current);
      previewTimer.current = null;
    }
  };
  useEffect(() => clearPreviewTimer, []);

  // Fresh editing session whenever a different layer's panel is opened.
  useEffect(() => {
    setDraft(committed);
    setSessionLabeling(committed);
    setDirty(false);
  }, [committed, layer?.id]);

  /* Geometry drives which placements are offered, so it must be resolved
     before the placement control can be trusted. */
  useEffect(() => {
    if (!open || !layer) return;

    const cached = layer.geometryType;
    if (cached && cached !== 'unknown') {
      setResolvedGeometry(cached);
      return;
    }

    let cancelled = false;
    setResolving(true);
    void resolveGeometryType(layer, module)
      .then((geometryType) => {
        if (cancelled) return;
        setResolvedGeometry(geometryType);
        if (geometryType !== 'unknown') onGeometryResolved?.(layer.id, geometryType);
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, layer?.id, layer?.geometryType]);

  /* Field list, from whichever source backs this layer. */
  useEffect(() => {
    if (!open || !layer) return;
    let cancelled = false;
    void fetchLabelFields(layer, module).then((result) => {
      if (!cancelled) setFields(result);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, layer?.id, module.arcgisUrl, module.geoServerUrl]);

  const pushPreview = useCallback(
    (next: LayerLabeling) => {
      clearPreviewTimer();
      previewTimer.current = window.setTimeout(() => {
        onPreview(next);
        previewTimer.current = null;
      }, PREVIEW_DEBOUNCE_MS);
    },
    [onPreview],
  );

  /** Applies a change to the edited class and previews the result. */
  const updateClass = (changes: Partial<LabelClass>) => {
    const next: LayerLabeling = {
      ...draft,
      classes: draft.classes.map((existing, index) =>
        index === 0 ? { ...existing, ...changes } : existing,
      ),
    };
    setDraft(next);
    setDirty(true);
    pushPreview(next);
  };

  const updateLabeling = (changes: Partial<LayerLabeling>) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    setDirty(true);
    pushPreview(next);
  };

  /* An SLD travels in the tile URL, which servers cap. Warn rather than let the
     map silently fall back to unlabelled tiles. */
  const sldTooLarge = useMemo(() => {
    if (labelingMode !== 'sld' || !layer) return false;
    const sld = buildLabelSld(layer.name, draft, geometry);
    return Boolean(sld) && isSldTooLarge(sld);
  }, [labelingMode, layer, draft, geometry]);

  const cancel = useCallback(() => {
    clearPreviewTimer();
    onPreview(sessionLabeling);
    setDraft(sessionLabeling);
    setDirty(false);
    onClose();
  }, [onClose, onPreview, sessionLabeling]);

  const apply = () => {
    clearPreviewTimer();
    onApply(draft);
    setSessionLabeling(draft);
    setDirty(false);
  };

  /** Back to label defaults. Symbology is untouched — different config entirely. */
  const reset = () => {
    const fresh = createLabeling(geometry);
    // Keep the layer labelled; resetting the STYLE should not silently switch
    // labels off and leave the user wondering where they went.
    fresh.enabled = draft.enabled;
    fresh.classes[0].field = labelClass.field;
    setDraft(fresh);
    setDirty(true);
    pushPreview(fresh);
  };

  // Escape cancels, matching the symbology panel.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, cancel]);

  const placements = PLACEMENTS_BY_GEOMETRY[geometry] ?? PLACEMENTS_BY_GEOMETRY.unknown;
  const isLine = geometry === 'line';
  const usingExpression = labelClass?.expression.trim() !== '';
  const unknownTokens = usingExpression
    ? extractExpressionFields(labelClass.expression).filter(
        (token) =>
          fields.length > 0 &&
          !fields.some((field) => field.name.toLowerCase() === token.toLowerCase()),
      )
    : [];
  const scaleProblem = labelClass ? describeScaleProblem(labelClass.scale) : null;

  return (
    <aside
      role="dialog"
      aria-label="Label properties"
      aria-hidden={!open}
      /* Desktop: right-hand floating panel over the map, same geometry as
         Symbology. Small screens: a bottom sheet measured against the VIEWPORT
         (`fixed`), because at phone widths the map column itself is only a
         hundred-odd pixels wide — anchoring to it would produce an unusable
         sliver. */
      className={`gl-card gl-surface--strong gl-surface--over-map z-[4000] flex flex-col overflow-hidden transition-transform duration-300 ease-out
        fixed inset-x-2 bottom-2 h-[72%]
        sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-2 sm:top-2 sm:h-[calc(100%-1rem)] sm:w-[340px] ${
          open
            ? 'translate-y-0 sm:translate-x-0'
            : 'pointer-events-none invisible translate-y-[calc(100%+1rem)] sm:translate-y-0 sm:translate-x-[calc(100%+1rem)]'
        }`}
      style={{
        borderRadius: 'var(--glass-radius-lg)',
        background: 'var(--glass-bg-strong)',
        // Only filter while shown — see SymbologyPanel: a backdrop-filter on a
        // hidden, always-mounted panel still blurs the map behind it.
        backdropFilter: open ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))' : undefined,
        WebkitBackdropFilter: open
          ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))'
          : undefined,
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--glass-shadow-lg)',
      }}
    >
      <header className="gl-header flex flex-shrink-0 items-start justify-between px-4 py-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[#0F2942]">Label Properties</h3>
          <p
            className="truncate text-xs text-slate-500"
            title={layer ? getLayerDisplayName(layer) : ''}
          >
            {layer ? getLayerDisplayName(layer) : '—'}
          </p>
          {layer && (
            <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
              {geometry === 'unknown' ? 'vector' : geometry}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={cancel}
          aria-label="Close label properties"
          className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!layer || !labelClass ? null : (
          <>
            {/* ── LABELING ── */}
            <PropertySection title="Labeling">
              <ToggleField
                label="Enable labels"
                checked={draft.enabled}
                onChange={(enabled) => updateLabeling({ enabled })}
              />
              <p className="mt-1 text-[10px] leading-snug text-slate-400">
                {describeLabelingMode(labelingMode)}
              </p>
            </PropertySection>

            {/* ── DATA ── */}
            <PropertySection title="Data" collapsible>
              <label className="block py-1.5">
                <span className="text-xs text-slate-600">Label field</span>
                <select
                  value={labelClass.field}
                  disabled={usingExpression}
                  onChange={(event) => updateClass({ field: event.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400 disabled:opacity-50"
                >
                  <option value="">-- Select a field --</option>
                  {fields.map((field) => (
                    <option key={field.name} value={field.name}>
                      {field.alias}
                    </option>
                  ))}
                </select>
                {fields.length === 0 && (
                  <span className="mt-1 block text-[10px] text-slate-400">
                    No field list available for this layer — use an expression instead.
                  </span>
                )}
              </label>

              <label className="block py-1.5">
                <span className="text-xs text-slate-600">Expression</span>
                <input
                  type="text"
                  value={labelClass.expression}
                  placeholder="{pipe_id} - {status}"
                  onChange={(event) => updateClass({ expression: event.target.value })}
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 font-mono text-xs outline-none focus:border-blue-400"
                />
                <span className="mt-1 block text-[10px] leading-snug text-slate-400">
                  Wrap field names in braces. Anything else is literal text. When set, this
                  replaces the field above.
                </span>
                {unknownTokens.length > 0 && (
                  <span className="mt-1 block text-[10px] text-amber-700">
                    Not a field on this layer: {unknownTokens.join(', ')}
                  </span>
                )}
              </label>

              <ToggleField
                label="Filter labels"
                checked={labelClass.filter.enabled}
                description="Label only the features that match"
                onChange={(enabled) =>
                  updateClass({ filter: { ...labelClass.filter, enabled } })
                }
              />
              {labelClass.filter.enabled && (
                <div className="mt-1 space-y-1.5">
                  <select
                    value={labelClass.filter.field}
                    aria-label="Filter field"
                    onChange={(event) =>
                      updateClass({ filter: { ...labelClass.filter, field: event.target.value } })
                    }
                    className="w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                  >
                    <option value="">-- Field --</option>
                    {fields.map((field) => (
                      <option key={field.name} value={field.name}>
                        {field.alias}
                      </option>
                    ))}
                  </select>
                  <div className="flex gap-1.5">
                    <select
                      value={labelClass.filter.operator}
                      aria-label="Filter operator"
                      onChange={(event) =>
                        updateClass({
                          filter: {
                            ...labelClass.filter,
                            operator: event.target.value as LabelFilterOperator,
                          },
                        })
                      }
                      className="w-28 flex-shrink-0 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                    >
                      {FILTER_OPERATORS.map((operator) => (
                        <option key={operator.id} value={operator.id}>
                          {operator.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="text"
                      value={labelClass.filter.value}
                      aria-label="Filter value"
                      placeholder="Value"
                      onChange={(event) =>
                        updateClass({ filter: { ...labelClass.filter, value: event.target.value } })
                      }
                      className="min-w-0 flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                    />
                  </div>
                </div>
              )}
            </PropertySection>

            {/* ── TEXT ── */}
            <PropertySection title="Text" collapsible>
              <label className="block py-1.5">
                <span className="text-xs text-slate-600">Font</span>
                <select
                  value={labelClass.text.fontFamily}
                  onChange={(event) =>
                    updateClass({ text: { ...labelClass.text, fontFamily: event.target.value } })
                  }
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                >
                  {LABEL_FONTS.map((font) => (
                    <option key={font.id} value={font.id}>
                      {font.label}
                    </option>
                  ))}
                </select>
              </label>

              <SliderField
                label="Size"
                value={labelClass.text.fontSize}
                min={8}
                max={36}
                suffix=" px"
                onChange={(fontSize) => updateClass({ text: { ...labelClass.text, fontSize } })}
              />

              <label className="block py-1.5">
                <span className="text-xs text-slate-600">Weight</span>
                <select
                  value={labelClass.text.fontWeight}
                  onChange={(event) =>
                    updateClass({
                      text: {
                        ...labelClass.text,
                        fontWeight: event.target.value as LabelClass['text']['fontWeight'],
                      },
                    })
                  }
                  className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                >
                  {FONT_WEIGHTS.map((weight) => (
                    <option key={weight.id} value={weight.id}>
                      {weight.label}
                    </option>
                  ))}
                </select>
              </label>

              <ToggleField
                label="Italic"
                checked={labelClass.text.fontStyle === 'italic'}
                onChange={(italic) =>
                  updateClass({
                    text: { ...labelClass.text, fontStyle: italic ? 'italic' : 'normal' },
                  })
                }
              />

              <ColorField
                label="Label color"
                value={labelClass.text.color}
                onChange={(color) => updateClass({ text: { ...labelClass.text, color } })}
              />
            </PropertySection>

            {/* ── HALO ── */}
            <PropertySection
              title="Halo"
              collapsible
              defaultOpen={false}
              trailing={
                <InfoHint
                  title="Halo"
                  content="An outline drawn behind the text. Without it, labels disappear into satellite imagery and dark basemaps."
                  label="About halos"
                />
              }
            >
              <ToggleField
                label="Enable halo"
                checked={labelClass.halo.enabled}
                description="Keeps text readable over imagery and dark basemaps"
                onChange={(enabled) => updateClass({ halo: { ...labelClass.halo, enabled } })}
              />
              {labelClass.halo.enabled && (
                <>
                  <ColorField
                    label="Halo color"
                    value={labelClass.halo.color}
                    onChange={(color) => updateClass({ halo: { ...labelClass.halo, color } })}
                  />
                  <SliderField
                    label="Halo width"
                    value={labelClass.halo.width}
                    min={0}
                    max={6}
                    step={0.5}
                    suffix=" px"
                    onChange={(width) => updateClass({ halo: { ...labelClass.halo, width } })}
                  />
                </>
              )}
            </PropertySection>

            {/* ── PLACEMENT ──
                Only the placements that make sense for THIS geometry are
                offered. There are no symbol options here at all: symbols are
                Symbology's job, not labeling's. */}
            <PropertySection title="Placement" collapsible defaultOpen={false}>
              {resolving ? (
                <p className="py-3 text-center text-xs text-slate-400">Detecting layer geometry…</p>
              ) : (
                <>
                  <label className="block py-1.5">
                    <span className="text-xs text-slate-600">Position</span>
                    <select
                      value={
                        placements.some((option) => option.id === labelClass.placement.type)
                          ? labelClass.placement.type
                          : defaultPlacementFor(geometry)
                      }
                      onChange={(event) =>
                        updateClass({
                          placement: {
                            ...labelClass.placement,
                            type: event.target.value as LabelClass['placement']['type'],
                          },
                        })
                      }
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                    >
                      {placements.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>

                  {isLine && (
                    <>
                      <ToggleField
                        label="Follow line direction"
                        checked={labelClass.placement.parallel}
                        onChange={(parallel) =>
                          updateClass({ placement: { ...labelClass.placement, parallel } })
                        }
                      />
                      <SliderField
                        label="Repeat every"
                        value={labelClass.placement.repeat}
                        min={0}
                        max={600}
                        step={25}
                        suffix={labelClass.placement.repeat === 0 ? ' px (once)' : ' px'}
                        onChange={(repeat) =>
                          updateClass({ placement: { ...labelClass.placement, repeat } })
                        }
                      />
                    </>
                  )}

                  <SliderField
                    label="Offset X"
                    value={labelClass.placement.offsetX}
                    min={-40}
                    max={40}
                    suffix=" px"
                    onChange={(offsetX) =>
                      updateClass({ placement: { ...labelClass.placement, offsetX } })
                    }
                  />
                  <SliderField
                    label="Offset Y"
                    value={labelClass.placement.offsetY}
                    min={-40}
                    max={40}
                    suffix=" px"
                    onChange={(offsetY) =>
                      updateClass({ placement: { ...labelClass.placement, offsetY } })
                    }
                  />

                  <Tooltip content="When two labels would collide, the first one placed keeps its spot and the other is dropped.">
                    <div>
                      <ToggleField
                        label="Avoid label overlap"
                        checked={labelClass.avoidOverlap}
                        onChange={(avoidOverlap) => updateClass({ avoidOverlap })}
                      />
                    </div>
                  </Tooltip>
                </>
              )}
            </PropertySection>

            {/* ── SCALE VISIBILITY ── */}
            <PropertySection
              title="Scale visibility"
              collapsible
              defaultOpen={false}
              trailing={
                <InfoHint
                  title="Scale visibility"
                  content="Controls the map scale range at which these labels are drawn. Outside the range the layer still shows — only its labels are hidden."
                  label="About scale visibility"
                />
              }
            >
              <ToggleField
                label="Scale dependent"
                checked={labelClass.scale.enabled}
                description={`Current map scale ${formatScale(currentScale)}`}
                onChange={(enabled) =>
                  updateClass({ scale: { ...labelClass.scale, enabled } })
                }
              />

              {labelClass.scale.enabled && (
                <div className="mt-2 space-y-2">
                  <label className="block">
                    <span className="text-[11px] text-slate-500">Preset</span>
                    <select
                      value=""
                      onChange={(event) => {
                        const preset = SCALE_PRESETS.find((item) => item.id === event.target.value);
                        if (!preset) return;
                        updateClass({
                          scale: {
                            enabled: true,
                            minScale: preset.minScale,
                            maxScale: preset.maxScale,
                          },
                        });
                      }}
                      className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
                    >
                      <option value="">-- Choose a preset --</option>
                      {SCALE_PRESETS.map((preset) => (
                        <option key={preset.id} value={preset.id}>
                          {preset.label} ({formatScale(preset.minScale)} –{' '}
                          {formatScale(preset.maxScale)})
                        </option>
                      ))}
                    </select>
                    <span className="mt-1 block text-[10px] text-slate-400">
                      A preset only fills the two values below; both stay editable.
                    </span>
                  </label>

                  <ScaleInput
                    label="Minimum scale (zoomed out)"
                    value={labelClass.scale.minScale}
                    currentScale={currentScale}
                    onChange={(minScale) =>
                      updateClass({ scale: { ...labelClass.scale, minScale } })
                    }
                  />
                  <ScaleInput
                    label="Maximum scale (zoomed in)"
                    value={labelClass.scale.maxScale}
                    currentScale={currentScale}
                    onChange={(maxScale) =>
                      updateClass({ scale: { ...labelClass.scale, maxScale } })
                    }
                  />

                  <p className="text-[10px] leading-snug text-slate-400">
                    Labels show between these two scales. Leave a box blank for no limit on
                    that side.
                  </p>
                  {scaleProblem && (
                    <p role="alert" className="rounded bg-amber-50 px-2 py-1.5 text-[10px] text-amber-800">
                      {scaleProblem}
                    </p>
                  )}
                </div>
              )}
            </PropertySection>

            {sldTooLarge && (
              <p
                role="alert"
                className="mx-4 mb-3 rounded bg-amber-50 px-2 py-1.5 text-[11px] leading-relaxed text-amber-800"
              >
                This label style is too long to send to GeoServer in a tile request, so labels
                are not being drawn. Shorten the expression or the filter value.
              </p>
            )}
          </>
        )}
      </div>

      <footer className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
        >
          Reset
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={cancel}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!dirty}
            className="rounded-lg bg-[#0F2942] px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </footer>
    </aside>
  );
}

/**
 * One scale bound.
 *
 * Kept as free text rather than a number input so "1:25,000" can be pasted
 * straight from ArcGIS; the value is only normalised on blur, otherwise typing
 * the second digit of "25000" would reformat the field under the cursor.
 */
function ScaleInput({
  label,
  value,
  currentScale,
  onChange,
}: {
  label: string;
  value: number;
  currentScale: number;
  onChange: (value: number) => void;
}) {
  const [text, setText] = useState(value > 0 ? formatScale(value) : '');
  useEffect(() => {
    setText(value > 0 ? formatScale(value) : '');
  }, [value]);

  return (
    <label className="block">
      <span className="flex items-center justify-between text-[11px] text-slate-500">
        {label}
        <button
          type="button"
          onClick={() => onChange(Math.round(currentScale))}
          className="text-[10px] font-semibold text-blue-600 hover:text-blue-800"
        >
          Use current
        </button>
      </span>
      <input
        type="text"
        inputMode="numeric"
        value={text}
        placeholder="No limit"
        aria-label={label}
        onChange={(event) => setText(event.target.value)}
        onBlur={() => onChange(parseScale(text))}
        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs outline-none focus:border-blue-400"
      />
    </label>
  );
}
